import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { leadLabel, leadPath, managedAtLeadSentence, type MaybeScope } from '@/lib/reporting-scope'

interface ManagedAtLeadCardProps {
  scope: MaybeScope
  /** What lives at the lead, in the operator's words: "This month on the books", "Targets". */
  what: string
  /** One sentence saying WHY, when the reason is not self-evident from `what`. */
  detail?: string
  /** Fixed suffix appended to the lead's backend-served basePath. "" = its dashboard. */
  leadSuffix?: string
  /** Overrides the default "Open <lead>" wording where a more specific verb reads better. */
  ctaLabel?: string
  className?: string
}

/**
 * WHERE THE NUMBER WENT — the card a MEMBER shows in place of a deferred surface.
 *
 * A member of a group has no revenue reporting and no pricing controls of its own: the unit is
 * measured and priced at the lead, so the backend sends those props as null rather than as zeros a
 * member would read as a hotel that has stopped selling. This is the other half of that contract.
 * It never renders an empty chart, never a dead control, and always names the hotel that has the
 * answer plus a link straight to the equivalent page there.
 *
 * Renders nothing when there is no group — so an ungrouped page cannot grow one of these.
 */
export default function ManagedAtLeadCard({
  scope,
  what,
  detail,
  leadSuffix = '',
  ctaLabel,
  className,
}: ManagedAtLeadCardProps) {
  if (!scope) return null

  // No link when the person cannot open the lead (change safety R4.3): the card still says where the
  // answer lives, without naming that listing or linking to it.
  const href = leadPath(scope, leadSuffix)

  return (
    <div className={cn('rounded-xl border border-border/60 bg-white p-5 shadow-sm', className)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {what} {isPlural(what) ? 'live' : 'lives'} at {leadLabel(scope)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{managedAtLeadSentence(scope)}</p>
          {detail && <p className="mt-1.5 max-w-prose text-xs text-muted-foreground">{detail}</p>}
        </div>

        {href && (
          <Link
            href={href}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
          >
            <Crown className="h-3.5 w-3.5" />
            {ctaLabel ?? `Open ${scope.leadPropertyName}`}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  )
}

// "Targets live at…" but "This month on the books lives at…". A crude test on purpose: the caller
// supplies the noun phrase, and the only shapes in use are a bare plural or a singular phrase.
function isPlural(what: string): boolean {
  const last = what.trim().split(/\s+/).pop() ?? ''
  return /s$/i.test(last) && !/ss$/i.test(last)
}
